import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { gsap } from 'gsap';

import useAudioPlayer from '@/shared/hooks/useAudioPlayer';

import countdownTickSfx from '@/features/laneChooserPhaser/assets/sounds/countdown-3.2.1.mp3';
import countdownGoSfx from '@/features/laneChooserPhaser/assets/sounds/countdown-go.mp3';

export type UseCountdownOptions = {
	initial?: number; // starting number, default 3
	onFinish?: () => void; // called after the countdown (and optional Start pause)
	startDelayAfterEndMs?: number; // how long to show "Start" before finishing, default 500ms
};

export type UseCountdownResult = {
	display: number | string | null; // null when not running or finished
	start: () => void;
};

const useCountdown = (options: UseCountdownOptions = {}): UseCountdownResult => {
	const { initial = 4, onFinish, startDelayAfterEndMs = 500 } = options;

	const [display, setDisplay] = useState<number | string | null>(null);
	const { play: playCountdownTick, stop: stopCountdownTick } = useAudioPlayer({
		src: countdownTickSfx,
		volume: 0.7,
	});
	const { play: playCountdownGo, stop: stopCountdownGo } = useAudioPlayer({
		src: countdownGoSfx,
		volume: 0.7,
	});
	const tlRef = useRef<gsap.core.Timeline | null>(null);

	const killTimeline = useCallback(() => {
		if (tlRef.current) {
			tlRef.current.kill();
			tlRef.current = null;
		}
	}, []);

	const buildTimeline = useCallback(() => {
		killTimeline();
		const tl = gsap.timeline();
		// Build numeric countdown from initial down to 1
		for (let i = initial, t = 0; i >= 1; i--, t += 1) {
			tl.call(() => setDisplay(i), [], t);
		}
		let currentTime = Math.max(0, initial);
		tl.call(() => setDisplay('Start'), [], currentTime);
		// then finish after specified delay
		const delaySec = Math.max(0, startDelayAfterEndMs) / 1000;
		currentTime += delaySec;
		// When finishing, clear running, keep display as-is (countdown overlay will be removed by parent)
		tl.call(
			() => {
				onFinish?.();
			},
			[],
			currentTime,
		);
		tlRef.current = tl;
		return tl;
	}, [initial, killTimeline, onFinish, startDelayAfterEndMs]);

	const start = useCallback(() => {
		const tl = buildTimeline();
		tl.play(0);
	}, [buildTimeline]);

	useEffect(() => {
		if (display == null) return;
		if (typeof display === 'number') {
			stopCountdownGo();
			stopCountdownTick();
			playCountdownTick();
		} else if (display === 'Start') {
			stopCountdownTick();
			stopCountdownGo();
			playCountdownGo();
		}
	}, [display, playCountdownGo, playCountdownTick, stopCountdownGo, stopCountdownTick]);

	useEffect(() => {
		return () => killTimeline();
	}, [killTimeline]);

	return useMemo(() => ({ display, start }), [display, start]);
};

export default useCountdown;
